import { AesMidasOptionChainDates } from '@orbis-systems/orbis-api-clients/aesmidas';
import { OptionChainDates } from '@orbis-systems/orbis-api-middleware';

import {
    BondQuote,
    Currency,
    PortfolioWithExtraInfo,
    Position,
    PositionType,
    PositionWithExtraInfo,
    Quote,
} from '@orbis-systems/orbis-api-middleware';

import { divide, multiply, subtract } from '@/utils/numberUtils';
import { DEFAULT_OPTION_CONTRACT_SIZE } from '@/utils/get-option-contract-size';

import { quoteQueries, QuoteEquityQueryParams } from './queries';
import { STALE_TIME_10_MIN } from '../constants';
import { queryClient } from '../api';
import { BondCountryFields } from './types';

export type GetQuoteEquityQueryDefinitionParams = QuoteEquityQueryParams;

export function getBondQuoteCountry(quote: BondCountryFields): string | undefined {
    return quote.country || quote.country_issue || quote.municipal_state || quote.municipalState;
}

export function getBondSearchYtw(bond: { ytw?: number | string; closeYtw?: number | string }): number | undefined {
    const raw = bond.ytw ?? bond.closeYtw;
    if (raw === undefined || raw === null || raw === '') return undefined;
    const value = Number(raw);
    return Number.isNaN(value) ? undefined : value;
}

export function getQuoteEquityQueryDefinition(params: GetQuoteEquityQueryDefinitionParams) {
    return quoteQueries.equity(params);
}

const emptyDates: OptionChainDates = {
    first: undefined,
    quarterlys: [],
    monthlies: [],
    weeklies: [],
    dates: [],
    leaps: [],
};

const isDateGroupValid = (category: string): category is keyof OptionChainDates => category in emptyDates;

export const formatMidasOptionChainDates = (
    data: AesMidasOptionChainDates | AesMidasOptionChainDates[],
): OptionChainDates => {
    const datesDictionary = Array.isArray(data) ? data[0]?.dates_dictionary : data.dates_dictionary;
    if (datesDictionary) {
        const groupedDates = Object.entries(datesDictionary).reduce((acc, [date, category]) => {
            if (category && isDateGroupValid(category)) {
                if (category === 'first') return { ...acc, first: date };
                else return { ...acc, [category]: [...(acc[category] || []), date].sort() };
            } else if (category === 'quarterlies')
                return { ...acc, quarterlys: [...(acc.quarterlys || []), date].sort() };
            else return acc;
        }, emptyDates);
        return groupedDates;
    } else return emptyDates;
};

export { DEFAULT_OPTION_CONTRACT_SIZE };
const DEFAULT_BOND_PAR_VALUE = 1000;

const getQuoteLastPrice = (quote: Quote | BondQuote): number | null => {
    if (quote.mutualFund && quote.navPrice) return quote.navPrice;
    else return quote.lastPrice ?? null;
};

// TODO: Support all quote/position price and quantity type combos.
// Currently assumes decimal quote price and position quantity in par.
const getBondPositionMarketValue = (params: { position: PositionWithExtraInfo; quote: BondQuote }): number | null => {
    const { position, quote } = params;
    const rawPrice = getQuoteLastPrice(quote);
    if (rawPrice === null || !position.quantity) return null;
    const parValue = quote.parValue ?? quote.par_value ?? DEFAULT_BOND_PAR_VALUE;
    const bondQuantity = divide(position.quantity, parValue);
    const pricePercent = multiply(rawPrice, 100);
    const principal = multiply(multiply(divide(pricePercent, 100), parValue), bondQuantity);
    const accruedInterest = quote.accruedInterest ?? quote.accrued_interest ?? 0;
    return principal + multiply(accruedInterest, bondQuantity);
};

const getPositionMarketValue = (params: {
    position: PositionWithExtraInfo;
    quote: Quote | BondQuote;
}): number | null => {
    const { position, quote } = params;
    if (!position.quantity) return null;
    else if (position.bond || quote.bond) return getBondPositionMarketValue({ position, quote: quote as BondQuote });
    const lastPrice = getQuoteLastPrice(quote);
    if (lastPrice == null) return null;
    const contractSize = position.option ? (quote.contractSize ?? DEFAULT_OPTION_CONTRACT_SIZE) : 1;
    return multiply(multiply(lastPrice, position.quantity), contractSize);
};

const applyPositionValueSign = (marketValue: number, position: Position): number => {
    if (position.short || position.type === PositionType.Short) return -Math.abs(marketValue);
    else return marketValue;
};

const calculatePositionGain = (params: {
    position: PositionWithExtraInfo;
    value: number;
    costValue: number;
}): { gain: number; gainPercent: number } => {
    const { value, costValue, position } = params;
    const gain =
        position.short || position.type === PositionType.Short ? costValue + value : subtract(value, costValue);
    const gainPercent = costValue !== 0 ? multiply(divide(gain, costValue), 100) : 0;
    return { gain, gainPercent };
};

const convertToBaseCurrency = (params: {
    positionCurrency?: string;
    baseCurrency: string;
    property: number;
    fx?: Quote;
}): number => {
    const { property, fx, positionCurrency, baseCurrency } = params;
    if (!fx?.lastPrice || !positionCurrency || positionCurrency === baseCurrency) return property;
    else return divide(property, fx.lastPrice);
};

const enrichPosition = (params: {
    position: PositionWithExtraInfo;
    quote: Quote | BondQuote;
    baseCurrency: string;
    fx?: Quote;
}): PositionWithExtraInfo => {
    const { position, quote, fx, baseCurrency } = params;
    const positionCurrency = quote.currency ?? position.currency;
    const baseEntry = { ...position, quote, ...(fx && { fx }) };
    if (position.value !== 0 || !position.quantity) return baseEntry;
    const unsignedMarketValue = getPositionMarketValue({ position, quote });
    if (unsignedMarketValue === null) return baseEntry;
    const signedValue = applyPositionValueSign(unsignedMarketValue, position);
    const value = convertToBaseCurrency({ property: signedValue, fx, positionCurrency, baseCurrency });
    const costValue = convertToBaseCurrency({ property: position.costValue, fx, positionCurrency, baseCurrency });
    const { gain, gainPercent } = calculatePositionGain({ value, costValue, position });
    return { ...baseEntry, costValue, value, gain, gainPercent };
};

export const enrichPortfolioWithMidasQuotes = async (params: {
    portfolio: PortfolioWithExtraInfo;
    staleTime?: number;
}) => {
    const { portfolio, staleTime = STALE_TIME_10_MIN } = params;
    const { equitySymbols, optionSymbols, bondSymbols } = portfolio.entries.reduce(
        (acc, pos) => {
            if (pos.option) acc.optionSymbols.add(pos.symbol);
            else if (pos.bond) acc.bondSymbols.add(pos.symbol);
            else acc.equitySymbols.add(pos.symbol);
            return acc;
        },
        {
            equitySymbols: new Set<string>(),
            optionSymbols: new Set<string>(),
            bondSymbols: new Set<string>(),
        },
    );

    const partitionedQuotes = await Promise.all([
        equitySymbols.size
            ? queryClient.fetchQuery({ ...quoteQueries.getMidasEquityQuotes([...equitySymbols]), staleTime })
            : [],
        optionSymbols.size
            ? queryClient.fetchQuery({ ...quoteQueries.getMidasOptionQuotes([...optionSymbols]), staleTime })
            : [],
        bondSymbols.size
            ? queryClient.fetchQuery({ ...quoteQueries.getMidasBondQuotes([...bondSymbols]), staleTime })
            : [],
    ]);

    const quotesBySymbol = partitionedQuotes.reduce<Record<string, Quote | BondQuote>>((acc, quotes) => {
        quotes.forEach((quote) => (acc[quote.symbol] = quote));
        return acc;
    }, {});

    const fxPairs = portfolio.entries.reduce<Set<string>>((pairs, position) => {
        const quoteCurrency = quotesBySymbol[position.symbol]?.currency ?? position.currency;
        if (quoteCurrency !== portfolio.currency) pairs.add(`${portfolio.currency}/${quoteCurrency}`);
        return pairs;
    }, new Set<string>());

    const fxRates = fxPairs.size
        ? await queryClient.fetchQuery({ ...quoteQueries.getMidasFxRates([...fxPairs]), staleTime })
        : [];

    const fxByCurrency = fxRates.reduce<Record<string, Quote>>((acc, fx) => {
        // UI expects position.fx.baseCurrency = portfolio.currency.
        acc[fx.quoteCurrency] = { ...fx, baseCurrency: portfolio.currency, currency: fx.quoteCurrency };
        return acc;
    }, {});

    return {
        ...portfolio,
        entries: portfolio.entries.map((position) => {
            const quote = quotesBySymbol[position.symbol] ?? position.quote ?? {};
            const currency = quote.currency ?? position.currency ?? Currency.UsDollar;
            const fx = currency !== portfolio.currency ? fxByCurrency[currency] : undefined;
            return enrichPosition({ position, quote, fx, baseCurrency: portfolio.currency });
        }),
    };
};

export const invalidateDirectMidasData = () => {
    return Promise.all([
        queryClient.invalidateQueries({ queryKey: [...quoteQueries.all().queryKey, 'getMidasEquityQuotes'] }),
        queryClient.invalidateQueries({ queryKey: [...quoteQueries.all().queryKey, 'getMidasOptionQuotes'] }),
        queryClient.invalidateQueries({ queryKey: [...quoteQueries.all().queryKey, 'getMidasBondQuotes'] }),
        queryClient.invalidateQueries({ queryKey: [...quoteQueries.all().queryKey, 'getMidasFxRates'] }),
    ]);
};

export const refetchDirectMidasData = () => {
    return Promise.all([
        queryClient.refetchQueries({ queryKey: [...quoteQueries.all().queryKey, 'getMidasEquityQuotes'] }),
        queryClient.refetchQueries({ queryKey: [...quoteQueries.all().queryKey, 'getMidasOptionQuotes'] }),
        queryClient.refetchQueries({ queryKey: [...quoteQueries.all().queryKey, 'getMidasBondQuotes'] }),
        queryClient.refetchQueries({ queryKey: [...quoteQueries.all().queryKey, 'getMidasFxRates'] }),
    ]);
};
